import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ownedError } from 'core/utils/owned-error';
import * as _ from 'lodash';
import { TableColumn } from 'shared/modules/table/table-utils/table-interfaces';

@Component({
  selector: 'app-column-select',
  templateUrl: './column-select.component.html',
  styleUrls: ['./column-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ColumnSelectComponent implements OnInit {
  constructor(private translateService: TranslateService) { }
  private _columns = new Array<TableColumn>();
  private _storageKey?: string;
  private _activeColumns = new Array<string>();
  private _inactiveColumns = new Array<string>();

  get storageKey(): string | undefined {
    return this._storageKey;
  }

  get inactiveColumns(): string[] {
    return this._inactiveColumns;
  }

  set inactiveColumns(value: string[]) {
    this._inactiveColumns = value;
  }

  get activeColumns(): string[] {
    return this._activeColumns;
  }

  set activeColumns(value: string[]) {
    this._activeColumns = value;
  }

  get columns(): TableColumn[] {
    return this._columns;
  }

  @Input()
  set columns(value: TableColumn[]) {
    this._columns = value;
  }

  @Input()
  set storageKey(value: string | undefined) {
    this._storageKey = value;
  }

  ngOnInit(): void {
    if (!this.columns) {
      throw ownedError(new Error(`Property 'columns' must be initialized`));
    }

    this.activeColumns = _.uniq(this.restoreColumns(this.columns, this.storageKey));
    this.inactiveColumns = this.columns.filter(c => !this.activeColumns.includes(c.name)).map(c => c.name);
  }

  drop(event: CdkDragDrop<string[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    }
    else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }

    if (!this.storageKey) {
      return;
    }

    this.storeColumns(this.activeColumns, this.storageKey);
  }

  getLabel(name: string): string {
    const column = this.columns.find(c => c.name === name);

    if (!column || !column.label) {
      return this.translateService.instant('COMMON.not_found');
    }

    return column.translateLabel ? this.translateService.instant(column.label) : column.label;
  }

  private storeColumns(columns: string[], storeKey: string): void {
    localStorage.setItem(storeKey, JSON.stringify(columns));
  }

  private restoreColumns(defaultColumns: TableColumn[], storeKey?: string): string[] {
    const jsonColumns = localStorage.getItem(storeKey || '');

    if (!jsonColumns) {
      return defaultColumns.filter(c => c.visible).map(c => c.name);
    }

    return JSON.parse(jsonColumns) as string[];
  }
}
